<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import Editor from '@tinymce/tinymce-vue';

const router = useRouter();
const isLoading = ref(false);
const isSending = ref(false);

// Form fields
const user = ref(null);
const toEmail = ref('mike.filip@aurorasoftware.com');
const subject = ref('');
const message = ref('');

// Error states
const subjectError = ref('');
const messageError = ref('');

// Get current user information
const fetchCurrentUser = () => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      user.value = JSON.parse(userJson);
    } catch (error) {
      console.error('Error parsing user data:', error);
      user.value = { email: 'Unknown user' };
    }
  } else {
    // Do nothing
  }
};

// Computed property for email sender display
const fromEmail = computed(() => {
  if (user.value) {
    return `${user.value.first_name || ''} ${user.value.last_name || ''} <${user.value.email}>`.trim();
  } else {
	// Need to comply with non-logged in setup.
  }
});

// Validate form before submission
const validateForm = () => {
  let isValid = true;
  
  if (!subject.value.trim()) {
    subjectError.value = 'Subject is required';
    isValid = false;
  } else {
    subjectError.value = '';
  }
  
  if (!message.value.trim()) {
    messageError.value = 'Message content is required';
    isValid = false;
  } else {
    messageError.value = '';
  }
  
  return isValid;
};

// Submit the contact form
const submitForm = async () => {
  if (!validateForm()) {
    return;
  }
  
  isSending.value = true;
  
  try {
    const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
		// Need to make this optional with someother kind of verification
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({
        from: user.value.email,
        to: toEmail.value,
        subject: subject.value,
        message: message.value,
        name: `${user.value.first_name || ''} ${user.value.last_name || ''}`.trim()
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to send message');
    }
    
    // Reset form fields on success
    subject.value = '';
    message.value = '';
    
    toast.success('Your message has been sent successfully');
  } catch (error) {
    console.error('Error sending message:', error);
    toast.error(error.message || 'Failed to send your message. Please try again later.');
  } finally {
    isSending.value = false;
  }
};

// Initialize on component mount
onMounted(() => {
  fetchCurrentUser();
});
</script>

<template>
  <div class="w-[calc(100vw-4rem)] flex-grow flex flex-col pl-6 pr-8 py-7 gap-y-8 overflow-y-scroll">
    <div class="p-2 bg-page">
      <h4 class="text-primary font-bold font-monster -ml-2">
        Contact Support
      </h4>
    </div>

    <div class="bg-white rounded-[14px] shadow-[2px_2px_6px_rgba(187,195,206,0.4)] p-6">
      <div class="max-w-3xl mx-auto">
        <!-- Info text -->
        <div class="mb-8">
          <p class="text-gray-600">
            Use this form to contact our support team. We'll respond to your inquiry as soon as possible.
          </p>
        </div>

        <form @submit.prevent="submitForm" class="space-y-6">
          <!-- From field (read-only) -->
          <div class="grid grid-cols-[100px_1fr] items-center">
            <label class="font-medium text-gray-700">From:</label>
            <input 
              type="text" 
              v-model="fromEmail" 
              readonly
              class="rounded-[10px] px-4 py-3 bg-gray-100 border-[1px] border-stroke font-monster border-solid outline-none w-full cursor-not-allowed"
            />
          </div>
          
          <!-- To field (read-only) -->
          <div class="grid grid-cols-[100px_1fr] items-center">
            <label class="font-medium text-gray-700">To:</label>
            <input 
              type="text" 
              v-model="toEmail" 
              readonly
              class="rounded-[10px] px-4 py-3 bg-gray-100 border-[1px] border-stroke font-monster border-solid outline-none w-full cursor-not-allowed"
            />
          </div>
          
          <!-- Subject field -->
          <div class="grid grid-cols-[100px_1fr] items-center">
            <label for="subject" class="font-medium text-gray-700">Subject:</label>
            <div class="w-full">
              <input 
                id="subject"
                type="text" 
                v-model="subject" 
                placeholder="Enter message subject"
                class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none w-full"
              />
              <p v-if="subjectError" class="mt-1 text-sm text-red-600">{{ subjectError }}</p>
            </div>
          </div>
          
          <!-- Message body with WYSIWYG editor -->
          <div class="grid grid-cols-1 items-start">
            <label for="message" class="font-medium text-gray-700 mb-2">Message:</label>
            <div class="w-full">
              <Editor
			  	api-key={{import.meta.env.PUBLIC_TINYMCE_API_KEY}}
                v-model="message"
                :init="{
                  height: 300,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }'
                }"
              />
              <p v-if="messageError" class="mt-1 text-sm text-red-600">{{ messageError }}</p>
            </div>
          </div>
          
          <!-- Submit button -->
          <div class="flex justify-end">
            <button 
              type="submit"
              :disabled="isSending"
              class="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-6 rounded-lg flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <span v-if="isSending" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
              Send Message
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>